import {useCallback, useEffect, useState} from 'react';
import {Box, Button, Flex, Heading, Spinner, Text} from '@chakra-ui/react';
import {CalendarIcon, ChevronDownIcon} from '@chakra-ui/icons';
import {useHalState} from '@synako/haligator';
import {useMe} from '@/haligator-factory';
import {Navigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {endOfMonth, format, startOfMonth, subMonths} from 'date-fns';
import NiceModal from '@ebay/nice-modal-react';
import {moisAnneeEnTexte, routes} from 'ff-common';
import {CodeLabelEGalim} from 'api-types/ffconnect';
import {EgalimSynthese} from './components/EgalimSynthese';
import {EgalimLabels} from './components/EgalimLabels';
import {EgalimPeriodeModale} from './components/EgalimPeriodeModale';
import {EgalimDetailLabels} from './components/EgalimDetailLabels';
import {EgalimExportButton} from './components/EgalimExportButton';

export const EgalimView: React.FC = () => {
  const {t} = useTranslation();
  const me = useMe();
  const meState = useHalState(me);

  // États pour la plage de dates (3 derniers mois par défaut)
  const [dateDebut, setDateDebut] = useState(
    format(startOfMonth(subMonths(new Date(), 2)), 'yyyy-MM-dd'),
  );
  const [dateFin, setDateFin] = useState(
    format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  );

  // Récupération de la synthèse Egalim (disable si le lien n'est pas disponible)
  const egalimDisponible = meState.state?.hasOneLink('synthese-egalim');
  const {client_id} = useParams<{client_id?: string}>();
  const syntheseNav = me.follow('synthese-egalim', {
    date_debut: dateDebut,
    date_fin: dateFin,
    client_id,
  });
  const {
    data: synthese,
    loading,
    refreshing,
  } = useHalState(syntheseNav, {disable: !egalimDisponible});
  const isLoading = loading || refreshing;

  // Labels éligibles actifs (mis à jour immédiatement au clic, puis synchronisé avec le serveur)
  const [labelsEligiblesActifs, setLabelsEligiblesActifs] = useState<
    CodeLabelEGalim[]
  >(synthese?.labels_eligibles_selectionnes ?? []);

  // Sync avec les données serveur après un refresh HAL
  useEffect(() => {
    setLabelsEligiblesActifs(synthese?.labels_eligibles_selectionnes ?? []);
  }, [synthese?.labels_eligibles_selectionnes]);

  const handleOpenModal = useCallback(() => {
    NiceModal.show(EgalimPeriodeModale, {
      onSelect: (periode: {date_debut: string; date_fin: string}) => {
        setDateDebut(periode.date_debut);
        setDateFin(periode.date_fin);
      },
      initialPeriode: {date_debut: dateDebut, date_fin: dateFin},
    });
  }, [dateDebut, dateFin]);

  // Guard : rediriger si le lien synthese-egalim n'est pas disponible
  if (!meState.loading && !egalimDisponible) {
    return (
      <Navigate
        to={routes.client.accueil.path}
        replace
      />
    );
  }

  return (
    <Box
      p={5}
      width='100%'
      bg='gray.bg'
    >
      <Heading
        size={{base: 'md', md: 'lg'}}
        mb={6}
        mt='10px'
        ml={{base: '30px', lg: 0}}
        color='black.text'
      >
        {t('egalim.titre')}
      </Heading>

      <Box
        p={{base: 4, md: 6}}
        bg='white'
        borderRadius='8px'
        w='100%'
      >
        <Flex
          direction={{base: 'column', md: 'row'}}
          justify='space-between'
          align={{base: 'flex-start', md: 'center'}}
        >
          <Text
            fontWeight='600'
            color='gray.dark'
            fontSize='md'
          >
            {t('egalim.synthese_titre')}
          </Text>
          <Button
            leftIcon={<CalendarIcon />}
            rightIcon={<ChevronDownIcon />}
            onClick={handleOpenModal}
            variant='outline'
            size='sm'
            w={{base: '100%', md: 'auto'}}
            mt={{base: 4, md: 0}}
          >
            {moisAnneeEnTexte(dateDebut, 0)} - {moisAnneeEnTexte(dateFin, 0)}
          </Button>
        </Flex>

        {isLoading && (
          <Flex
            justify='center'
            py={8}
          >
            <Spinner color='black.text' />
          </Flex>
        )}

        {!isLoading && synthese && (
          <>
            {/* Synthèse (gauche) + Labels (droite) */}
            <Flex
              direction={{base: 'column', lg: 'row'}}
              gap={6}
              mt={4}
            >
              <Box flex='1'>
                {/* Synthèse Egalim avec les barres */}
                <EgalimSynthese synthese={synthese} />
                {/* Export Excel */}
                <EgalimExportButton syntheseNav={syntheseNav} />
              </Box>
              <Box flex='1'>
                {/* Labels ÉGalim (officiels et éligibles non officiels) */}
                <EgalimLabels
                  syntheseNav={syntheseNav}
                  labelsSelectionnes={labelsEligiblesActifs}
                  onLabelsEligiblesChange={setLabelsEligiblesActifs}
                />
              </Box>
            </Flex>

            {/* Détails par label avec donut */}
            <EgalimDetailLabels
              synthese={synthese}
              labelsSelectionnes={labelsEligiblesActifs}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
